import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CalendarDatePicker } from '@/components/calendar-date-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useBeforeUnload, useNavigate, useLocation } from 'react-router-dom';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import {
  Info, Loader2, AlertTriangle, CheckCircle, FileDown, XCircle, Clock, FileSpreadsheet, FileArchive, History
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { downloadBillingReport } from '@/api/billing/billing.api';
import { BillingReportParams } from '@/api/billing/billing.types';

enum ProcessingStage {
  IDLE = 'idle',
  PREPARING = 'preparing',
  PROCESSING = 'processing',
  DOWNLOADING = 'downloading',
  COMPLETE = 'complete',
  ERROR = 'error',
}

interface ReportGeneratorProps {
  onError: (error: string) => void;
}

export const ReportGenerator = ({ onError }: ReportGeneratorProps) => {
  const { toast } = useEnhancedToast();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPathname = useRef(location.pathname);

  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });
  const [processing, setProcessing] = useState<boolean>(false);
  const [stage, setStage] = useState<ProcessingStage>(ProcessingStage.IDLE);
  const [navigationBlocked, setNavigationBlocked] = useState<boolean>(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [loadingSteps, setLoadingSteps] = useState<number>(0);
  const [loadingMessages] = useState<string[]>([
    "Gathering your billing data...",
    "Processing transaction history...",
    "Compiling usage metrics and charges...",
    "Finalizing your report for download..."
  ]);

  // Handle navigation attempts while processing
  useBeforeUnload(
    useCallback(
      (event) => {
        if (navigationBlocked) {
          event.preventDefault();
          return "Your billing report is still being processed. If you leave now, the download will be cancelled.";
        }
      },
      [navigationBlocked]
    )
  );

  // Monitor location changes
  useEffect(() => {
    if (processing && location.pathname !== initialPathname.current) {
      setShowLeaveDialog(true);
      setPendingNavigation(location.pathname);
      navigate(initialPathname.current, { replace: true });
    }
  }, [location, processing, navigate]);

  // Rotate through loading messages
  useEffect(() => {
    if (processing && stage !== ProcessingStage.COMPLETE && stage !== ProcessingStage.ERROR) {
      const interval = setInterval(() => {
        setLoadingSteps(prev => (prev + 1) % loadingMessages.length);
      }, 3500);
      return () => clearInterval(interval);
    }
  }, [processing, stage, loadingMessages.length]);

  const handleDateSelect = useCallback((range: { from: Date | undefined; to: Date | undefined }) => {
    setDate({ from: range.from, to: range.to });
  }, []);

  const processAndDownload = async () => {
    if (!date?.from || !date?.to) {
      toast({
        title: 'Date selection required',
        description: 'Please select a date range to download the billing report.',
        variant: 'error',
        icon: AlertTriangle
      });
      return;
    }

    try {
      setProcessing(true);
      setNavigationBlocked(true);
      setStage(ProcessingStage.PREPARING);

      const startDate = format(date.from, 'yyyy-MM-dd');
      const endDate = format(date.to, 'yyyy-MM-dd');
      
      const params: BillingReportParams = {
        startDate,
        endDate
      };

      // Processing simulation with real API call
      setTimeout(() => {
        setStage(ProcessingStage.PROCESSING);
        
        setTimeout(() => {
          setStage(ProcessingStage.DOWNLOADING);
          
          setTimeout(async () => {
            try {
              const result = await downloadBillingReport(params);
              
              if ('url' in result) {
                setStage(ProcessingStage.COMPLETE);
                
                const link = document.createElement('a');
                link.style.display = 'none';
                link.href = result.url;
                link.setAttribute('download', result.filename);
                
                document.body.appendChild(link);
                link.click();
                
                setTimeout(() => {
                  window.URL.revokeObjectURL(result.url);
                  document.body.removeChild(link);
                }, 1000);
                
                toast({
                  title: 'Download complete',
                  description: `Your billing report has been downloaded successfully as a ${result.fileType === 'zip' ? 'ZIP archive' : 'spreadsheet'}.`,
                  variant: 'success',
                  icon: CheckCircle
                });
              } else {
                setStage(ProcessingStage.ERROR);
                toast({
                  title: 'Error downloading report',
                  description: result.error || 'Failed to download the billing report.',
                  variant: 'error',
                  icon: XCircle
                });
              }
            } catch (err) {
              setStage(ProcessingStage.ERROR);
              toast({
                title: 'Error processing report',
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
                variant: 'error',
                icon: XCircle
              });
            }
            
            setTimeout(() => {
              setProcessing(false);
              setNavigationBlocked(false);
              setStage(ProcessingStage.IDLE);
            }, 1500);
          }, 1000);
        }, 1000);
      }, 1000);
      
    } catch (error) {
      setStage(ProcessingStage.ERROR);
      onError('Failed to generate billing report. Please try again.');
      
      setTimeout(() => {
        setProcessing(false);
        setNavigationBlocked(false);
        setStage(ProcessingStage.IDLE);
      }, 3000);
    }
  };

  const getStageInfo = () => {
    switch (stage) {
      case ProcessingStage.PREPARING:
        return { title: 'Preparing Data', description: loadingMessages[loadingSteps] };
      case ProcessingStage.PROCESSING:
        return { title: 'Processing Report', description: loadingMessages[loadingSteps] };
      case ProcessingStage.DOWNLOADING:
        return { title: 'Finalizing Download', description: loadingMessages[loadingSteps] };
      case ProcessingStage.COMPLETE:
        return { title: 'Download Complete', description: 'Your report has been downloaded successfully.' };
      case ProcessingStage.ERROR:
        return { title: 'Processing Error', description: 'There was an error generating your billing report.' };
      default:
        return { title: '', description: '' };
    }
  };

  const getProgressValue = () => {
    switch (stage) {
      case ProcessingStage.PREPARING: return 25;
      case ProcessingStage.PROCESSING: return 50;
      case ProcessingStage.DOWNLOADING: return 75;
      case ProcessingStage.COMPLETE: return 100;
      default: return undefined;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.05 }}
    >
      <Card className="shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <FileDown className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Generate Billing Report</CardTitle>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="w-[280px] p-0">
                  <div className="px-3 py-2 border-b bg-muted/30">
                    <p className="font-semibold text-sm">Report Details</p>
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <History className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium">Usage Data</p>
                        <p className="text-xs text-muted-foreground">Detailed charges and call history</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium">Excel Format</p>
                        <p className="text-xs text-muted-foreground">Spreadsheet (.xlsx) for analysis</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <FileArchive className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium">Large Datasets</p>
                        <p className="text-xs text-muted-foreground">ZIP archive for bulk data</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 border-t bg-muted/20">
                    <p className="text-xs text-muted-foreground">
                      ⏱️ Processing may take a few minutes
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <CalendarDatePicker
                id="report-date-range"
                className="w-full h-9"
                date={date as DateRange}
                onDateSelect={handleDateSelect}
                numberOfMonths={2}
                variant="outline"
                align="center"
              />
            </div>
            <Button
              size="sm"
              className="w-full sm:w-auto gap-2"
              disabled={processing || !date?.from || !date?.to}
              onClick={processAndDownload}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Generate & Download
                </>
              )}
            </Button>
          </div>

          <AnimatePresence>
            {processing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4 p-4 bg-muted/50 rounded-lg"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary animate-pulse" />
                    <p className="text-sm font-medium">{getStageInfo().title}</p>
                  </div>
                  {stage === ProcessingStage.COMPLETE && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                </div>
                
                <Progress value={getProgressValue()} className="h-2" />
                
                <p className="text-sm text-muted-foreground">
                  {getStageInfo().description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Leave Page Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
              Processing in Progress
            </DialogTitle>
            <DialogDescription>
              Your billing report is still being processed. If you leave now, the download will be cancelled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>
              Stay on Page
            </Button>
            <Button onClick={() => {
              setProcessing(false);
              setNavigationBlocked(false);
              setShowLeaveDialog(false);
              if (pendingNavigation) navigate(pendingNavigation);
            }}>
              Leave Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
